/**
 * IMPORTS
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import {
  handleContarNotificacoesNaoLidas,
  handleListarNotificacoes,
  handleMarcarNotificacaoLida,
  handleMarcarTodasNotificacoesLidas,
} from "@/domain/use-cases/conversa-cliente";
import { FEATURES, useFeature } from "@/utils/auth/features";
import { destravarAudioNotificacao, tocarSomNotificacao } from "@/utils/tocar-som-notificacao";

const NOTIFICACOES_QUERY_KEY = "notificacoes";

/** Intervalo do polling da conversa e da lista; pausa sem foco (focusManager no ReactQueryProvider). */
const INTERVALO_ATUALIZACAO_MS = 30_000;

/** Contador: a cada 1 minuto; sem foco da janela o polling pausa (focusManager). */
const INTERVALO_CONTAGEM_MS = 60_000;

/**
 * Contador de não lidas (sino, item do menu e som). Só consulta com a feature liberada.
 * Cada componente com `refetchInterval` agenda o próprio timer, então só UM deve fazer o
 * polling (`polling: true`, no MonitorSomNotificacoes do layout); os demais só leem o cache.
 */
function useContagemNotificacoes({ polling = false }: { polling?: boolean } = {}) {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const habilitado = useFeature(FEATURES.ACESSAR_NOTIFICACOES);

  return useQuery({
    queryKey: [NOTIFICACOES_QUERY_KEY, "contagem"],
    queryFn: () => handleContarNotificacoesNaoLidas(accessToken!),
    enabled: Boolean(accessToken && habilitado),
    refetchInterval: polling ? INTERVALO_CONTAGEM_MS : false,
    refetchOnWindowFocus: true,
  });
}

/** Lista paginada ("carregar mais"), mais recentes primeiro. */
function useListaNotificacoes(itensPorPagina: number, habilitado = true) {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  return useInfiniteQuery({
    queryKey: [NOTIFICACOES_QUERY_KEY, "lista", itensPorPagina],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => handleListarNotificacoes(accessToken!, pageParam, itensPorPagina),
    getNextPageParam: (ultima) =>
      ultima.pagination.current_page < ultima.pagination.last_page
        ? ultima.pagination.current_page + 1
        : undefined,
    enabled: Boolean(accessToken && habilitado),
    refetchInterval: habilitado ? INTERVALO_ATUALIZACAO_MS : false,
  });
}

function useMarcarNotificacoes() {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const queryClient = useQueryClient();
  const invalidar = () => queryClient.invalidateQueries({ queryKey: [NOTIFICACOES_QUERY_KEY] });

  const marcarLida = useMutation({
    mutationFn: (id: number) => handleMarcarNotificacaoLida(accessToken!, id),
    onSuccess: invalidar,
  });
  const marcarTodas = useMutation({
    mutationFn: () => handleMarcarTodasNotificacoesLidas(accessToken!),
    onSuccess: invalidar,
  });

  return { marcarLida, marcarTodas };
}

const CHAVE_SOM_ATIVO = "letscom_som_notificacoes";
const EVENTO_PREFERENCIA = "letscom:som-notificacoes";

function lerSomAtivo(): boolean {
  try {
    return localStorage.getItem(CHAVE_SOM_ATIVO) !== "0";
  } catch {
    return true;
  }
}

function inscreverPreferencia(aoMudar: () => void) {
  window.addEventListener("storage", aoMudar);
  window.addEventListener(EVENTO_PREFERENCIA, aoMudar);
  return () => {
    window.removeEventListener("storage", aoMudar);
    window.removeEventListener(EVENTO_PREFERENCIA, aoMudar);
  };
}

/** Preferência de som deste navegador (sincronizada entre abas e componentes). */
function usePreferenciaSomNotificacoes() {
  const somAtivo = useSyncExternalStore(inscreverPreferencia, lerSomAtivo, () => true);

  const alternarSom = useCallback(() => {
    try {
      localStorage.setItem(CHAVE_SOM_ATIVO, lerSomAtivo() ? "0" : "1");
    } catch {
      // sem storage (aba anônima/bloqueado): mantém o padrão
    }
    window.dispatchEvent(new Event(EVENTO_PREFERENCIA));
    destravarAudioNotificacao();
  }, []);

  return { somAtivo, alternarSom };
}

/**
 * Toca o som quando as não lidas aumentam (nunca na primeira carga).
 * Deve ser usado em um único ponto sempre montado (o layout), para não tocar em dobro.
 */
function useSomNotificacoes(naoLidas: number | undefined) {
  const { somAtivo } = usePreferenciaSomNotificacoes();
  const anteriorRef = useRef<number | undefined>(undefined);

  // libera o áudio na primeira interação (regra de autoplay dos navegadores)
  useEffect(() => {
    const destravar = () => destravarAudioNotificacao();
    window.addEventListener("pointerdown", destravar, { once: true });
    window.addEventListener("keydown", destravar, { once: true });
    return () => {
      window.removeEventListener("pointerdown", destravar);
      window.removeEventListener("keydown", destravar);
    };
  }, []);

  useEffect(() => {
    if (naoLidas === undefined) return;
    const anterior = anteriorRef.current;
    anteriorRef.current = naoLidas;
    if (somAtivo && anterior !== undefined && naoLidas > anterior) {
      tocarSomNotificacao();
    }
  }, [naoLidas, somAtivo]);
}

/**
 * EXPORTS
 */
export {
  INTERVALO_ATUALIZACAO_MS,
  NOTIFICACOES_QUERY_KEY,
  useContagemNotificacoes,
  useListaNotificacoes,
  useMarcarNotificacoes,
  usePreferenciaSomNotificacoes,
  useSomNotificacoes,
};
